import {
  mapServerScanStatusToClient,
  getCorrelationIdFromS3Key,
} from '@ublend-npm/aula-editor';
import * as actionTypes from '../../constants/actions';
import formatPost from '../../api/posts/formatPost';
import formatUser from '../../api/posts/formatUser';
import formatTag from '../../api/tags/formatTag';
import formatTagPost from '../../api/tags/formatTagPost';
import { createTagPostSuccess, deleteTagPostSuccess } from '../tags/tagPost';
import { createTagSuccess } from '../tags/tag';
import { Entities } from '../../utils/commentsEntityTypes';
import removeCommentFromQueryData from '../../comments/utils/removeCommentFromQueryData';
import addCommentToQueryData from '../../comments/utils/addCommentToQueryData';

export const registerLiveWithQueryClient =
  (queryClient) => (socket, dispatch, getState) => {
    const normalizeScanStatus = (status) => {
      const value = String(status).toLowerCase().trim();
      if (value.includes('success') || value.includes('clean')) {
        return 'CLEAN';
      }
      if (
        value.includes('scanning') ||
        value.includes('pending') ||
        value.includes('progress')
      ) {
        return 'SCANNING';
      }
      if (value.includes('infected')) {
        return 'INFECTED';
      }
      if (
        value.includes('failed') ||
        value.includes('error') ||
        value.includes('failure')
      ) {
        return 'ERROR';
      }
      return value.toUpperCase();
    };
    const onPostAdded = (post) => {
      const {
        data: { classRooms },
      } = getState();
      const { id, space } = post;
      if (classRooms[space].posts.indexOf(id) > -1) {
        return;
      }
      dispatch({
        type: actionTypes.CLASSROOM_CREATE_POST,
        status: 'success',
        post: formatPost(post),
      });
    };

    const onPostDeleted = ({ id }) => {
      const {
        data: { posts },
      } = getState();
      const post = posts[id];
      if (!post) {
        return;
      }

      dispatch({
        type: actionTypes.CLASSROOM_DELETE_POST,
        status: 'success',
        post,
      });
    };

    const onPostUpdated = (post) => {
      dispatch({
        type: actionTypes.CLASSROOM_EDIT_POST,
        status: 'success',
        post: formatPost(post, post.tagPosts),
      });
    };

    const onCommentAdded = (comment) => {
      const {
        data: { comments, [Entities[comment.entityType]]: entities },
      } = getState();

      if (comments[comment.id]) {
        return;
      }
      if (!entities[comment.entityId]) {
        return;
      }

      if (queryClient) {
        addCommentToQueryData(queryClient, comment);
      }

      dispatch({
        type: actionTypes.COMMENTS_CREATE,
        status: 'success',
        payload: {
          comment: {
            ...comment,
            user: formatUser(comment.user),
          },
        },
      });
    };

    const onCommentDeleted = ({ id }) => {
      const {
        data: { comments },
      } = getState();
      const comment = comments[id];
      if (!comment) {
        return;
      }

      if (queryClient) {
        removeCommentFromQueryData(queryClient, comment);
      }

      dispatch({
        type: actionTypes.COMMENTS_DELETE,
        status: 'success',
        comment,
      });
    };

    const onCommentUpdated = (comment) => {
      dispatch({
        type: actionTypes.COMMENTS_EDIT,
        status: 'success',
        comment: {
          ...comment,
          user: formatUser(comment.user),
        },
      });
    };

    const onTagCreated = (tag) => createTagSuccess(formatTag(tag), dispatch);

    const onTagAddedToPost = (tagPost) =>
      createTagPostSuccess(formatTagPost(tagPost), dispatch);

    const onTagRemovedFromPost = (payload) =>
      deleteTagPostSuccess(payload, dispatch);

    const onFileScanComplete = (payload) => {
      // PushEvent may deliver fields at top level OR nested in payload.data

      console.log('[EVENT_SUCCESS_FIRED WEB-APP] RECEIVED', payload);
      const data = payload?.data || payload || {};
      const fileId = payload?.fileId ?? data?.fileId;
      const s3Key = payload?.s3Key ?? data?.s3Key;
      const status = payload?.status ?? data?.status;

      const { correlationId, filename } = getCorrelationIdFromS3Key(s3Key);

      if ((fileId == null && s3Key == null) || status == null) {
        return;
      }

      const normalizedStatus = mapServerScanStatusToClient(
        normalizeScanStatus(status)
      );
      dispatch({
        type: actionTypes.FILE_SCAN_COMPLETE,
        payload: {
          ...(fileId != null && { id: fileId }),
          ...(s3Key != null && { s3Key }),
          scanStatus: normalizedStatus,
          correlationId,
          filename,
        },
      });
    };

    socket.on('posts:PostAdded', onPostAdded);
    socket.on('posts:PostDeleted', onPostDeleted);
    socket.on('posts:PostUpdated', onPostUpdated);
    socket.on('posts:CommentAdded', onCommentAdded);
    socket.on('posts:CommentDeleted', onCommentDeleted);
    socket.on('posts:CommentUpdated', onCommentUpdated);
    socket.on('materials:CommentAdded', onCommentAdded);
    socket.on('materials:CommentDeleted', onCommentDeleted);
    socket.on('materials:CommentUpdated', onCommentUpdated);
    socket.on('Tag:TagCreated', onTagCreated);
    socket.on('Tag:TagAddedToPost', onTagAddedToPost);
    socket.on('Tag:TagRemovedFromPost', onTagRemovedFromPost);
    socket.on('File:ScanComplete', onFileScanComplete);
    socket.on('File:Scanning:scanning', onFileScanComplete);
    socket.on('File:Scanning:success', onFileScanComplete);
    socket.on('File:Scanning:infected', onFileScanComplete);
    socket.on('File:Scanning:error', onFileScanComplete);
    socket.on('File:Scanning:failed', onFileScanComplete);
  };

const registerLive = registerLiveWithQueryClient(null);

export default registerLive;
